import React, { useEffect, useState, useRef, MutableRefObject } from "react";
import { m, LazyMotion, domMax, AnimatePresence } from "framer-motion";
import useSubtitleModal from "./useSubtitleModal";
import SubtitlesDownloadMenu from "./SubtitlesDownloadMenu";
import { Fragment, Subtitle, VideoState } from "core/models";
import {
    Modal,
    Button,
    VideoPlayer,
    PlayerButtons,
    SubtitleCard,
    ScrubBar,
    Icon,
    ConfirmActionModal,
    Spinner,
    Dropdown,
    SubtitleTimeline,
} from "ui/components";
import formatDuration from "utils/formatDuration";
import { cx } from "utils/join-classnames";
import * as captionConsts from "core/constants/captions";
import $ from "./SubtitlesModal.module.scss";

interface Props {
    fragment: Fragment;
    onClose(): void;
    onSave: (editedFragment: Fragment, notificationId: string) => void;
}

const SubtitlesModal: React.FC<Props> = ({ fragment, onClose, onSave }) => {
    const subtitlelistRef =
        useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;
    const {
        subtitles,
        language,
        pending,
        uploadPending,
        videoPlayerRef,
        activeSubtitle,
        videoPlayerRequest,
        subtitleTrackSrc,
        error,
        playing,
        handleDownload,
        changeLanguage,
        handlePlayerButtonClick,
        setVideoPlayerRequest,
        timeUpdate,
        addSubtitle,
        checkEnoughDistance,
        saveSubtitlecardChange,
        deleteSubtitle,
        setActiveSubtitle,
        upload,
        setPlaying,
    } = useSubtitleModal(fragment, subtitlelistRef);

    // Should this be in the useSubtitleModal hook?
    const [useButtonShortcuts, setUseButtonShortcuts] = useState<boolean>(true);
    const [subtitleHovered, setSubtitleHovered] = useState<Subtitle>();

    useEffect(() => {
        // Keeps active subtitle element visible
        if (!activeSubtitle) {
            setUseButtonShortcuts(true);
        }
    }, [activeSubtitle, subtitles]);

    // Modal close confirm check, will move this to a useModal hook later
    const [confirmClose, setConfirmClose] = useState<boolean>(false);
    const checkNeedToConfirm = () => {
        if (subtitles.length > 0) {
            setConfirmClose(true);
        } else {
            onClose();
        }
    };

    const startUpload = async (): Promise<void> => {
        const uploadResult = await upload();
        if (uploadResult) {
            onSave(uploadResult.updatedFragment, uploadResult.notificationId);
        }
    };

    const videoOverlay = (videoState: VideoState): React.ReactNode =>
        videoState ? (
            <div className={$.duration}>
                {formatDuration(videoState.duration)}
            </div>
        ) : null;

    const handleSubtitleClick = (sub: Subtitle) => {
        setActiveSubtitle(sub);
        setVideoPlayerRequest({
            type: "setTime",
            value: sub.startTime,
        });
    };
    const handleSubtitleHover = (sub: Subtitle) => {
        setSubtitleHovered(sub);
    };

    const modalContent = (
        <div className={$.content}>
            {pending && (
                <div className={$.pendingOverlay}>
                    <Spinner /> Gathering caption info
                </div>
            )}
            <div className={$.content__left}>
                <div className={$.dropdownHolder}>
                    <span>Language:</span>
                    <Dropdown
                        theme="dark"
                        options={captionConsts.languageOptions}
                        onValueChanged={changeLanguage}
                        value={language}
                        className={$.languageDropdown}
                    />
                </div>
                <div className={$.videoHolder}>
                    <VideoPlayer
                        type="video"
                        videoSrc={fragment.getMediaURL()}
                        controls={false}
                        actionRequest={videoPlayerRequest}
                        subtitleTrackSrc={subtitleTrackSrc}
                        onTimeUpdate={timeUpdate}
                        externalVideoRef={videoPlayerRef}
                        overlay={videoOverlay}
                        onPlaying={(status: boolean) => setPlaying(status)}
                    />
                </div>
                {!pending && (
                    <>
                        <div className={$.scrubbarContainer}>
                            <ScrubBar
                                vidRef={videoPlayerRef}
                                size="large"
                                expanded
                                showCurrentTime
                            />
                        </div>
                        <SubtitleTimeline
                            videoRef={videoPlayerRef}
                            activeSubtitle={activeSubtitle}
                            subtitles={subtitles}
                            onSubtitleClick={handleSubtitleClick}
                            onSubtitleHover={handleSubtitleHover}
                        />

                        <div className={$.buttonbar}>
                            <PlayerButtons
                                onButtonClick={handlePlayerButtonClick}
                                playing={playing}
                                trimButtons
                                trimButtonsDisabled={!activeSubtitle}
                                useShortcuts={useButtonShortcuts}
                                essentialsOnly
                            />
                            <Button
                                type="secondary"
                                icon="plus"
                                size="small"
                                onClick={() =>
                                    addSubtitle(
                                        0,
                                        videoPlayerRef.current.currentTime,
                                    )
                                }
                                className={$.addBtn}
                            >
                                Caption
                            </Button>
                        </div>
                    </>
                )}
            </div>

            <div className={$.content__right} ref={subtitlelistRef}>
                {subtitles
                    .sort(
                        (a: Subtitle, b: Subtitle) => a.startTime - b.startTime,
                    )
                    .map((subtitle, index) => {
                        const notEnoughDistance = !checkEnoughDistance(index);
                        return (
                            <LazyMotion
                                features={domMax}
                                strict
                                key={subtitle.id}
                            >
                                <AnimatePresence>
                                    <m.div
                                        className={$.subtitleSection}
                                        key={subtitle.id}
                                        layout
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                    >
                                        <div
                                            className={cx(
                                                $.addBtn,
                                                notEnoughDistance
                                                    ? $["addBtn--disabled"]
                                                    : null,
                                            )}
                                            onClick={() => {
                                                if (notEnoughDistance) {
                                                    return;
                                                }
                                                addSubtitle(index, 0);
                                            }}
                                            role="presentation"
                                        >
                                            <Button
                                                type="function"
                                                icon="plus"
                                                size="small"
                                                disabled={notEnoughDistance}
                                            />
                                            {index === 0 &&
                                                (notEnoughDistance
                                                    ? "No space for caption"
                                                    : "Add before")}
                                            {index !== 0 &&
                                                (notEnoughDistance
                                                    ? "No space for caption"
                                                    : "Add in between")}
                                        </div>
                                        <SubtitleCard
                                            subtitle={subtitle}
                                            onChange={(
                                                changedSubtitle: Subtitle,
                                            ) => {
                                                saveSubtitlecardChange(
                                                    changedSubtitle,
                                                    index,
                                                );
                                                setVideoPlayerRequest({
                                                    type: "setTime",
                                                    value: changedSubtitle.startTime,
                                                });
                                            }}
                                            onDelete={() =>
                                                deleteSubtitle(index)
                                            }
                                            onFocus={(
                                                target:
                                                    | "startTime"
                                                    | "endTime"
                                                    | "text",
                                            ) => {
                                                setActiveSubtitle(subtitle);
                                                setUseButtonShortcuts(false);
                                                setVideoPlayerRequest({
                                                    type: "setTimePaused",
                                                    value:
                                                        target === "endTime"
                                                            ? subtitle.endTime
                                                            : subtitle.startTime,
                                                });
                                            }}
                                            onBlur={() =>
                                                setUseButtonShortcuts(true)
                                            }
                                            isActive={
                                                activeSubtitle?.id ===
                                                subtitle.id
                                            }
                                            isHovered={
                                                subtitleHovered?.id ===
                                                subtitle.id
                                            }
                                        />
                                        {subtitle.error && (
                                            <div className={$.subtitleError}>
                                                <Icon
                                                    name="error"
                                                    className={
                                                        $.subtitleError__icon
                                                    }
                                                />{" "}
                                                {subtitle.error}
                                            </div>
                                        )}
                                    </m.div>
                                </AnimatePresence>
                            </LazyMotion>
                        );
                    })}
                {!subtitles.length && !pending && (
                    <div className={$.emptyState}>
                        {captionConsts.emptyState}
                    </div>
                )}

                {!pending && (
                    <div
                        className={cx(
                            $.addBtn,
                            !checkEnoughDistance(subtitles.length)
                                ? $["addBtn--disabled"]
                                : null,
                        )}
                        onClick={() => {
                            if (!checkEnoughDistance(subtitles.length)) {
                                return;
                            }
                            addSubtitle(subtitles.length, 0);
                        }}
                        role="presentation"
                    >
                        <Button
                            type="function"
                            icon="plus"
                            size="small"
                            disabled={!checkEnoughDistance(subtitles.length)}
                        />
                        {!checkEnoughDistance(subtitles.length)
                            ? "No space for caption"
                            : "Add after"}
                    </div>
                )}
            </div>
        </div>
    );

    const subtitlesDownloadMenu = (
        <SubtitlesDownloadMenu
            disabled={!subtitles || subtitles.length === 0}
            handleDownload={handleDownload}
        />
    );

    return (
        <>
            <Modal
                content={modalContent}
                lineColor="amber"
                theme="dark"
                onSave={startUpload}
                onClose={checkNeedToConfirm}
                saveButtonText={uploadPending ? "Saving…" : "Save"}
                saveButtonDisabled={
                    pending || subtitles.length === 0 || error || uploadPending
                }
                fullSize
                title={`Captions for '${fragment.title}'`}
                icon="subtitles"
                extraButton={!pending && subtitlesDownloadMenu}
            />
            {confirmClose && (
                <ConfirmActionModal
                    title="Confirm close"
                    description="You will lose all changes"
                    handleCancel={() => setConfirmClose(false)}
                    handleConfirm={onClose}
                    confirmBtnText="Confirm"
                    theme="dark"
                />
            )}
        </>
    );
};

export default SubtitlesModal;
